import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

import { Add, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { Navigate } from 'react-router-dom';
import { Fab, Tooltip } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  FeatureSource,
  // FloatingFeature,
  ModelBase, 
  PlayerObject,
} from "@models";
import { 
  // Condition,
  Creature,
  SourceBook, 
  // Spell 
} from "@dndModels";
import { DATA_TYPE_INFO, FEATURE_SOURCE_TYPES } from "@dndModels/Constants";
import { PLAYER_OBJECT_TYPES } from "@dndModels/DataTypeConstants";
import { DataType, SourceBookType } from "@dndModels/Types";

// import DataUtilities from "../../utilities/common_utilities";
import DandDAPI from "@dndUtilities/system_api";
// import DandDCharacterUtilities from '../../systems/d&d/utilities/character_utilities';

import CheckBox from "@components/input/CheckBox";
import LoadingBox from "@components/input/LoadingBox";
import ObjectIndexItem from "@components/Navigation/ObjectIndexItem";

import ButtonBox from "@components/input/buttons/ButtonBox";
import ProgressBar from "@components/input/ProgressBar";
import SelectSourceBookBox from "@dndComponents/select/SelectSourceBookBox";
import FilterBox from "@dndComponents/filtering/FilterBox";
import LetterLinks from "@components/Navigation/LetterLinks";
import PageLinks from "@components/Navigation/PageLinks";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import InfoButton from "@components/input/buttons/InfoButton";
import { useAuth } from 'react-oidc-context';

interface Props { 
  data_type: DataType;
  true_filter?: any;
  extra_filter?: (obj: ModelBase | PlayerObject) => boolean;
  feature_source_type?: string;
  exclude_options?: string[];
  details?: boolean;
  special_copy?: (obj: ModelBase | PlayerObject) => void;
  special_update?: (obj: ModelBase | PlayerObject, onSuccess: () => void) => void;
  extra?: (obj: ModelBase | PlayerObject) => React.ReactNode;
  name_display?: (obj: ModelBase | PlayerObject) => React.ReactNode;
  disabled?: boolean;
  constants?: any;
  show_image?: boolean;
  save_all?: boolean;
  log_it?: boolean;
  empty_features?: boolean;
  onDelete?: (_id: string) => void;
  extra_by_add?: () => React.ReactNode;
  onAdd?: () => void;
  quick_fields?: string[];
  get_filtered?: (filtered: string[]) => void;
}

// const api = API.getInstance();
// const common_util = DataUtilities.getInstance();
const api = DandDAPI.getInstance();
// const char_util = DandDCharacterUtilities.getInstance();

export default function ObjectIndex({
  data_type,
  feature_source_type = "",
  exclude_options = [],
  details = false,
  true_filter = {},
  extra_filter,
  special_copy,
  special_update,
  // no_description = false,
  extra,
  name_display,
  disabled = false,
  constants = {},
  show_image = false,
  save_all = false,
  log_it = false,
  empty_features = false,
  onDelete,
  extra_by_add,
  quick_fields,
  onAdd,
  get_filtered
}: Props) {
  const auth = useAuth();
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loading_archive, setLoadingArchive] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loading_message, setLoadingMessage] = useState("");
  const [percentage, setPercentage] = useState(0);
  const [archives, setArchives] = useState(false);
  const [archived_sb_id, setArchivedSBId] = useState(api.working_on_sb ? api.working_on_sb._id : "");
  const [archived_sb_type, setArchivedSBType] = useState<SourceBookType | "">(api.working_on_sb ? api.working_on_sb.source_book_type : "");
  const [page_num, setPageNum] = useState(0);
  const [objects, setObjects] = useState<(ModelBase | PlayerObject)[]>([]);
  const [filtered, setFiltered] = useState<(ModelBase | PlayerObject)[]>([]);
  const [archived_objects, setArchivedObjects] = useState<ModelBase[]>([]);
  const [saving_all, setSavingAll] = useState(false);
  const [saved, setSaved] = useState<string[]>([]);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [filter, setFilter] = useState<any>({});
  const [filtering, setFiltering] = useState(false);
  const [sort_field, setSortField] = useState<string>("name");
  const [sort_descending, setSortDescending] = useState(false);
  const [display_info, setDisplayInfo] = useState(api.user ? !api.user.hide_info.includes(data_type) : true);
  const prev_data_type = useRef<DataType | "">("");

  const dt_info = DATA_TYPE_INFO.find(o => o.data_type === data_type);
  
  const extra_fields = useMemo(() => {
    return dt_info?.extra_fields || [];
  }, [dt_info]);

  const loadArchives = (id: string) => {
    if (!loading && api) {
      setLoadingArchive(true);
      if (log_it) {
        debugger;
      }
      const use_filter = { ...true_filter };
      api.getObjects({
        data_type, 
        filter: use_filter,
        ignore_templates: false, archives: [id]
      }).then(res => {
        let new_objects = res as ModelBase[];
        new_objects = new_objects.filter(o => o.source_book_ids.includes(id));
        if (extra_filter) {
          // console.log("extra filter");
          new_objects = new_objects.filter(o => extra_filter(o));
        }
        setArchivedObjects(new_objects.sort((a, b) => { return a.name.localeCompare(b.name); }));
        setLoadingArchive(false);
        setRefreshing(true);
      });
    }
  }

  useEffect(() => {
    const load = async () => {
      if (log_it) {
        debugger;
      }
      if (PLAYER_OBJECT_TYPES.includes(data_type)) {
        if (api.user) { // No Point trying to load player objects if they're not logged in.
          const onComplete = (res: PlayerObject[]) => {
            let new_objects = [...res];
            if (extra_filter) {
              // console.log("extra filter");
              new_objects = new_objects.filter(o => extra_filter(o));
            }
            setObjects(new_objects.sort((a, b) => { return a.name.localeCompare(b.name); }));
            setLoading(false);
            setRefreshing(true);
          };
          const onProgress = (data_type: DataType, percent: number) => {
            setPercentage(percent * 100);
            setLoadingMessage(`Loading ${dt_info?.plural??""}`);
          };
          console.log(true_filter);
          api.getPlayerObjects({ 
            data_type, 
            filter: (feature_source_type === "" ? { ...true_filter } : { ...true_filter, feature_source_type }),
            onProgress, onComplete
          });
        } else {
          setTimeout(() => {
            console.error("No User");
            setLoading(false);
          }, 100);
        }
      } else {
        const onComplete = (res: ModelBase[]) => {
          let new_objects = [...res];
          if (extra_filter) {
            // console.log("extra filter");
            new_objects = new_objects.filter(o => extra_filter(o));
          }
          // console.log(new_objects);
          setObjects(new_objects.sort((a, b) => { return a.name.localeCompare(b.name); }));
          setLoading(false);
          setRefreshing(true);
        };
        const onProgress = (data_type: DataType, percent: number) => {
          setPercentage(percent * 100);
          setLoadingMessage(`Loading ${dt_info?.plural??""}`);
        };
        const onError = (message: string) => {
          setPercentage(0);
          setLoadingMessage(message);
        };
        // debugger;
        if (data_type === "source_book") {
          const res = await api.getSourceBooks({ 
            filter: (feature_source_type === "" ? { ...true_filter } : { ...true_filter, feature_source_type }),
            onProgress, log_it
          });
          onComplete(res);
        } else {
          const res = await api.getObjectsWithCallbacks<ModelBase>({ 
            data_type, 
            filter: (feature_source_type === "" ? { ...true_filter } : { ...true_filter, feature_source_type }),
            onProgress, onError, log_it
          });
          onComplete(res);
        }
      }
    }
    if (!auth.isLoading && (!auth.isAuthenticated || api.user) && data_type !== prev_data_type.current) {
      prev_data_type.current = data_type;
      setLoading(true);
      setFetched(true);
      // debugger;
      load();
    }
  }, [data_type, fetched, auth.isLoading, auth.isAuthenticated, api.user]);

  const sorter = useCallback((a: any, b: any) => {
    let primary_sort = 0;
    
    // Handle favorite sorting for PlayerObjects
    if (a instanceof PlayerObject && b instanceof PlayerObject && sort_field === "name") {
      if (a.is_favorite !== b.is_favorite) {
        return a.is_favorite ? -1 : 1;
      }
    }

    // Primary sort by the selected field
    if (sort_field === "name") {
      const a_name = a instanceof ModelBase ? a.display_format : a.name;
      const b_name = b instanceof ModelBase ? b.display_format : b.name;
      primary_sort = a_name.trim().localeCompare(b_name.trim());
    } else {
      // Sort by extra field
      const a_value: any = (a as any)[sort_field];
      const b_value: any = (b as any)[sort_field];
      
      // // If there's a display function, use it to get the sortable value
      // if (field_config?.display) {
      //   a_value = field_config.display(a);
      //   b_value = field_config.display(b);
      // }
      
      // Handle different types
      if (typeof a_value === 'number' && typeof b_value === 'number') {
        primary_sort = a_value - b_value;
      } else {
        const a_str = String(a_value || '');
        const b_str = String(b_value || '');
        primary_sort = a_str.localeCompare(b_str);
      }
    }

    // Apply descending if needed
    if (sort_descending) {
      primary_sort = -primary_sort;
    }

    // Secondary sort by name (ascending) if primary sort is equal and not sorting by name
    if (primary_sort === 0 && sort_field !== "name") {
      const a_name = a instanceof ModelBase ? a.display_format : a.name;
      const b_name = b instanceof ModelBase ? b.display_format : b.name;
      return a_name.trim().localeCompare(b_name.trim());
    }

    return primary_sort;
  }, [sort_field, sort_descending]);

  const refilter = useCallback(() => {
    // debugger;
    let new_filtered: (ModelBase | PlayerObject)[] = []; 
    if (archives && data_type !== "source_book" && dt_info && !dt_info.player_object) {
      new_filtered = archived_objects; // .filter(o => o.source_book_ids.includes(archived_sb_id));
    } else {
      new_filtered = objects;
    }
    const the_filter = {...filter};
    if (the_filter.start_letter) {
      the_filter["name*startsWith"] = the_filter.start_letter;
      delete the_filter.start_letter;
    }
    if (log_it) {
      debugger;
    }
    // debugger;
    // console.log(new_filtered);
    // console.log(the_filter);
    new_filtered = api.filter(data_type, new_filtered, the_filter) as (ModelBase | PlayerObject)[];
    // console.log(new_filtered);
    if (new_filtered.length > 0) {
      if (data_type === "source_book" || (dt_info && dt_info.player_object)) {
        new_filtered = new_filtered.filter(o => o.archived === archives);
      }
      new_filtered = new_filtered.sort(sorter);
    }
    if (empty_features) {
      if (FEATURE_SOURCE_TYPES.includes(data_type)) {
        const feature_sources = new_filtered as FeatureSource[];
        new_filtered = feature_sources.filter(o => o.feature_bases.filter(o2 => o2.features.filter(o => o.floating_feature === null).length > 0).length > 0);
      } else if (data_type === "creature") {
        const creatures = new_filtered as Creature[];
        new_filtered = creatures.filter(o => 
          o.action_fbs.filter(o2 => o2.features.filter(o => o.floating_feature === null).length > 0).length > 0 ||
          o.legendary_action_fbs.filter(o2 => o2.features.filter(o => o.floating_feature === null).length > 0).length > 0 ||
          o.lair_action_fbs.filter(o2 => o2.features.filter(o => o.floating_feature === null).length > 0).length > 0 ||
          o.traits.filter(o2 => o2.features.filter(o => o.floating_feature === null).length > 0).length > 0
        );
        // new_filtered = creatures.filter(o => o.actions.filter(o => o.floating_feature === null).length > 0 || o.legendary_actions.filter(o => o.floating_feature === null).length > 0 || o.special_abilities.filter(o => o.floating_feature === null).length > 0);
      }
    }
    setFiltered(new_filtered);
    if (get_filtered) {
      get_filtered(new_filtered.map(o => o._id));
    }
  }, [archives, archived_objects, data_type, empty_features, filter, dt_info, get_filtered, log_it, objects, sorter]);

  useEffect(() => {
    if (refreshing) {
      setRefreshing(false);
      refilter();
    }
  }, [refreshing, refilter]);

  useEffect(() => {
    if (filtering) {
      refilter();
      setFiltering(false);
    }
  }, [filtering, refilter]);

  const refresh = () => {
    setRefreshing(true);
  }

  useEffect(() => {
    if (saved.length < filtered.length) {
      saveNext();
    } else {
      setSavingAll(false);
    }
    // eslint-disable-next-line
  }, [saved]);

  const saveNext = () => {
    if (saving_all) {
      const saveMe = filtered.filter(o => !saved.includes(o._id))[0];
      if (saveMe) {
        api.updateObject(data_type, saveMe).then(res => {
          const new_saved = [...saved];
          new_saved.push(saveMe._id);
          setSaved(new_saved);
        });
      } else {
        setSavingAll(true);
      }
    }
  }

  const saveAll = async () => {
    debugger;
    if (saving_all) {
      setSavingAll(false);
    } else {
      const saveMe = filtered[0];
      if (saveMe) {
        setSavingAll(true);
        if (saveMe instanceof ModelBase) {
          await api.loadObject(saveMe);
        }
        await api.updateObject(data_type, saveMe)
        setSaved([saveMe._id]);
      }
    }
  }

  const renderSavingAll = () => {
    if (saving_all) {
      return (
        <Grid
          className="padded"
          size={{
            xs: 12
          }}>
          <ProgressBar value={saved.length} max={filtered.length} color='Green' display_progress="counts" />
        </Grid>
      );
    }
    return null;
  }

  const updateFilter = (the_filter: any, startLetter = "") => {
    // debugger;
    const new_filter = {...the_filter};
    
    if (startLetter !== "") {
      new_filter["name*startsWith"] = startLetter;
    } else {
      delete new_filter["name*startsWith"];
    }

    setPageNum(0);
    setFilter(new_filter);
    setFiltering(true);
  }

  const handleHeaderClick = (field: string) => {
    if (sort_field === field) {
      // Toggle descending for the same field
      setSortDescending(!sort_descending);
    } else {
      // New field, start with ascending
      setSortField(field);
      setSortDescending(false);
    }
    setRefreshing(true);
  }

  const renderColumnHeaders = () => {
    if (filtered.length === 0) {
      return null;
    }

    // debugger;
    const hasExtraFields = extra_fields.length > 0;
    const nameSize = hasExtraFields ? 12 - (extra_fields.length * 2) : 12;

    return (
      <Grid
        size={{
          xs: 12
        }}>
        <Grid container spacing={0} direction="row" className="tableHeader" style={{ marginBottom: '4px' }}>
          <Grid
            className="padded clickable"
            onClick={() => handleHeaderClick("name")}
            size={{
              xs: nameSize
            }}>
            Name
            {sort_field === "name" && (
              sort_descending ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />
            )}
          </Grid>
          {extra_fields.map((field, index) => (
            <Grid
              key={index}
              className="padded clickable centered"
              onClick={() => handleHeaderClick(field.field)}
              size={{
                xs: 2
              }}>
              {field.label}
              {sort_field === field.field && (
                sort_descending ? <ArrowDownward fontSize="small" /> : <ArrowUpward fontSize="small" />
              )}
            </Grid>
          ))}
        </Grid>
      </Grid>
    );
  }

  const toggleInfo = () => {
    const user = api.user;
    if (user) {
      if (display_info) {
        user.hide_info.push(data_type);
      } else {
        user.hide_info = user.hide_info.filter(o => o !== data_type);
      }

      api.upsertObject("user", user);
    }
    setDisplayInfo(!display_info);
  }

  const render = () => {
    if (!fetched || loading || auth.isLoading) {
      return (<LoadingBox message={loading_message} percentage={percentage} size={window.innerHeight * 0.5} height={window.innerHeight - 64} width={window.innerWidth - 24} />); // width={window.innerWidth - 17}
    } else if (navigateTo !== null) {
      return <Navigate to={navigateTo} />;
    } else if (!dt_info) {
      return <>Invalid Data Type</>;
    } else {
      const height = window.innerHeight - 221;
      const page_size = window.innerWidth >= 1000 ? Math.floor(height / 36.5) : 10;

      // if (data_type === "floating_feature" && filtered[0] instanceof FloatingFeature) {
      //   console.log((filtered as FloatingFeature[]).map(o => o.feature_type));
      // }
      // console.log(objects);
      
      const page_count = Math.ceil(filtered.length / page_size);
      const filtered_and_paged: (ModelBase | PlayerObject)[] = filtered.slice(page_size * page_num, page_size * (page_num + 1));

      return (
        <Grid container spacing={0} direction="row">
          <Helmet>
            <title>Homebrew Labs - { dt_info.plural }</title>
          </Helmet>
          <Grid size={3}>
            <span className="header padded">
              { dt_info.plural }
              { dt_info.info &&
                <InfoButton onClick={toggleInfo} />
              }
            </span>
            { !exclude_options.includes("Add") && api.user && api.online && 
              <Tooltip title={`Create New ${dt_info.name}`}>
                <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                  onClick={ () => {
                    if (onAdd) {
                      onAdd();
                    } else {
                      setNavigateTo(`/dandd/${data_type}/create`);
                    }
                  }}>
                  <Add/>
                </Fab>
              </Tooltip> 
            }
            { api.user && extra_by_add && extra_by_add() }
          </Grid>
          <Grid size={9}>
            <FilterBox 
              data_type={data_type}
              onChange={updateFilter}
              onLoad={updateFilter}
              // log_it
              all_objects={objects}
              quick_fields={quick_fields}
            />
          </Grid>
          { dt_info.info && display_info &&
            <Grid size={12}>
              <DisplayStringWithReplacements display_string={dt_info.info} />
            </Grid>
          }
          <Grid
            size={{
              xs: archives && data_type !== "source_book" && !dt_info.player_object ? 3 : 12
            }}>
            <CheckBox
              name="View Archives"
              value={archives}
              disabled={!api.online}
              onChange={() => {
                setArchives(!archives);
                setRefreshing(true);
              }}
            />
          </Grid>
          { archives && data_type !== "source_book" && !dt_info.player_object &&
            <Grid size={9}>
              <SelectSourceBookBox
                name="Archived Source Book"
                id={archived_sb_id}
                type={archived_sb_type}
                archives
                onChange={(type, id) => {
                  setArchivedSBType(type);
                  const old_id = archived_sb_id;
                  setArchivedSBId(id);
                  if (id !== old_id) {
                    loadArchives(id);
                  }
                }}
              />
            </Grid>
          }
          { save_all && api.online && api.user && api.user.admin &&
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <ButtonBox
                  name={ saving_all ? "Cancel" : "Save All" }
                  onClick={saveAll}
                />
              </Grid>
              { saving_all && 
                <Grid
                  className="header centered"
                  size={{
                    xs: 12
                  }}>Saving All</Grid> 
              }
            </>
          }
          { renderSavingAll() }
          { loading_archive ? 
            <Grid size={12}>
              <LoadingBox />
            </Grid>
          :
            <>
              <Grid size={12}>
                <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
              </Grid>
              { renderColumnHeaders() }
              <Grid size={12}>
                <Grid container spacing={0} direction="row">
                  { !saving_all && !refreshing && filtered_and_paged.map((obj) => {
                    return (
                      <Grid
                        key={obj._id}
                        size={12}>
                        <ObjectIndexItem 
                          obj={obj} 
                          data_type={data_type}
                          disabled={processing || disabled}
                          onProcessingChange={setProcessing}
                          refresh={refresh}
                          // alternate={ key % 2 === 0 }
                          constants={constants}
                          onDelete={(_id: string) => {
                            const new_objects = objects.filter(o => o._id !== _id);
                            if (onDelete) {
                              onDelete(_id);
                            }
                            setObjects(new_objects);
                            setRefreshing(true);
                          }}
                          special_copy={special_copy}
                          special_update={special_update}
                          feature_source_type={feature_source_type}
                          no_edit={exclude_options.includes("Edit")}
                          details={details}
                          show_image={show_image}
                          extra={extra}
                          log_it={log_it}
                          name_display={name_display}
                          extra_fields={extra_fields}
                          onArchiveChange={() => {
                            if (obj instanceof PlayerObject || obj instanceof SourceBook) {
                              if (obj.is_archived) {
                                const new_objects = objects.filter(o => o._id !== obj._id);
                                setObjects(new_objects);
                              } else {
                                let new_objects = archived_objects.filter(o => o._id !== obj._id);
                                setArchivedObjects(new_objects);
                                new_objects = [...(objects as ModelBase[])];
                                new_objects.push(obj as ModelBase);
                                setObjects(new_objects);
                              }
                              setRefreshing(true);
                            }
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
              <Grid size={12}>
                <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
              </Grid>
              <Grid size={12}>
                <LetterLinks onChange={(changed: string) => {
                  updateFilter(filter, changed);
                }} />
              </Grid>
            </>
          }
        </Grid>
      ); 
    } 
  }

  return render();
}